import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { TFunction } from "i18next";

interface DeleteViewModalProps {
  t: TFunction<"global", undefined>;
  open: boolean;
  viewName: string;
  viewId: string;
  onClose: () => void;
  onConfirm: (viewId: string) => void;
  isDeleting: boolean;
}

const DeleteViewModal: React.FC<DeleteViewModalProps> = ({
  t,
  open,
  viewName,
  viewId,
  onClose,
  onConfirm,
  isDeleting,
}) => {
  const handleConfirm = async () => {
    await onConfirm(viewId);
  };

  return (
    <Dialog
      open={open}
      onClose={!isDeleting ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DeleteIcon color="error" />
          <Typography variant="h6" component="span">
            {t("Data_views.delete_view")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t("Data_views.delete_confirmation_message", { viewName })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Data_views.delete_warning")}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isDeleting}
          sx={{ minWidth: 100 }}
        >
          {t("Data_views.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={
            isDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteIcon />
            )
          }
          sx={{ minWidth: 120 }}
        >
          {isDeleting ? t("Data_views.deleting") : t("Data_views.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteViewModal;
